import React, { type Dispatch, type FC, type SetStateAction } from 'react';
import { Portal } from 'react-portal';
import { FormValues, useFormField } from '@ynap/form';
import Overlay, { StopPropagation } from '@ynap/overlay';
import { AddAddressAction } from './@types';
import { AddressForm } from '../AddressForm';
import { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';
import { mapAddressValues } from '../AddressForm/utils';
import { TrackingProp } from 'react-tracking';

type AnyFn = (...args: any[]) => any;

interface AddAddressRouteProps {
    config: Config;
    addTitle: string;
    locale: Locale;
    history: { push: (url: string) => void };
    messages: Messages;
    match: { path: string; url: string };
    addAddress?: AddAddressAction;
    trackConfirmAddNewAddress?: () => void;
    withOverlay?: boolean;
    isCheckoutLayout?: boolean;
    formTitle?: string;
    setEditAddressID?: AnyFn;
    onSubmit?: AnyFn;
    hideCountry?: boolean;
    tracking?: TrackingProp;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    removeAddressFinder?: (...args: any[]) => any;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
}

const AddAddressRoute: FC<AddAddressRouteProps> = ({
    config,
    addTitle,
    locale,
    history,
    messages,
    match,
    addAddress,
    trackConfirmAddNewAddress,
    withOverlay = true,
    isCheckoutLayout,
    formTitle,
    setEditAddressID,
    onSubmit,
    hideCountry,
    tracking,
    primaryCategory,
    checkoutAnalytics,
    removeAddressFinder,
    setRemoveAddressFinder,
}) => {
    const [addressIdField, setAddressIdField] = useFormField('addressId');

    const handleSubmit = (values: FormValues) => {
        if (addAddress) {
            const mappedValues = mapAddressValues(values, locale);
            trackConfirmAddNewAddress?.();
            return addAddress(mappedValues).then(({ payload: { addressId } }) => {
                // Need to update the currently selected address
                setEditAddressID?.('');
                setAddressIdField({ ...addressIdField, value: addressId });
                if (!isCheckoutLayout) {
                    history.push(match.url);
                } else {
                    onSubmit?.({ ...values, addressId });
                }
            });
        }
    };

    const renderContent = () => {
        return (
            <AddressForm
                config={config}
                country={locale.country}
                onSubmit={withOverlay || isCheckoutLayout ? handleSubmit : undefined}
                messages={messages}
                isCheckoutLayout={isCheckoutLayout}
                formTitle={formTitle}
                hideCountry={hideCountry}
                tracking={tracking}
                primaryCategory={primaryCategory}
                checkoutAnalytics={checkoutAnalytics}
                removeAddressFinder={removeAddressFinder}
                setRemoveAddressFinder={setRemoveAddressFinder}
            />
        );
    };

    if (withOverlay) {
        return (
            <Portal>
                <StopPropagation>
                    <Overlay title={addTitle} onClose={() => history.push(match.url)}>
                        {renderContent()}
                    </Overlay>
                </StopPropagation>
            </Portal>
        );
    }

    return renderContent();
};

export default AddAddressRoute;
